import React, { useCallback, useEffect, useMemo, useState } from "react";
import BreadCrumb from "Common/BreadCrumb";
import { Search, X, Download, RefreshCw, Image, MapPin } from "lucide-react";
import TableContainer from "Common/TableContainer";
import Modal from "Common/Components/Modal";
import { toast, ToastContainer } from "react-toastify";
import TableDropDownSmall from "pages/Components/TableDropDownSmall";
import { exportToExcel } from "Common/Components/Excel";
import LoadingCounter from "Common/Components/LoadingCounterProps";
import PaginationTest from "Common/Components/Paginationtest";
import axios from "axios";
import StaticMapMarker from "Common/Components/StaticArcMap";
import ImageLightbox from "Common/Components/ImageLightbox";
import Swal from "sweetalert2"; // اضافه شد
import { useAssistantFilter } from "Common/Assistant/lib/pageFilter";

// ========== Types ==========
interface OrderImage {
    id: number;
    url: string;
    file_name: string;
    category: string;
}

interface ViolationReport {
    id: number;
    status: string;
    city: string;
    baker_name: string;
    out_of_network: number;
    under_baking: number;
    distribution_stage: string;
    quota: number;
    discharge_date_jalali: string;
    unloaded_count: number;
    driver_images: OrderImage[];
    inspection_date_jalali: string;
    inspector_counted_count: number;
    inspector_images: OrderImage[];
    time_gap_days: number;
    allowed_consumption: number;
    driver_location_lat: number | null;
    driver_location_lng: number | null;
    inspector_location_lat: number | null;
    inspector_location_lng: number | null;
    location_match: string;
    baker_code: number; // اضافه شد
}

interface ApiResponse {
    total: number;
    size: number;
    pages: number;
    items: ViolationReport[];
}

// تابع ساده برای ترجمه خطا (اختیاری)
const translateErrorMessage = (message: string): string => {
    if (!message) return "خطای ناشناخته";
    const errorMap: { [key: string]: string } = {
        "Unauthorized": "احراز هویت ناموفق. لطفاً مجدداً وارد شوید.",
        "Forbidden": "شما دسترسی به این عملیات را ندارید.",
        "Not found": "داده‌ای یافت نشد.",
        "Internal server error": "خطای داخلی سرور. لطفاً بعداً تلاش کنید.",
    };
    for (const [en, fa] of Object.entries(errorMap)) {
        if (message.includes(en)) return fa;
    }
    return message;
};

const ViolationReports = () => {
    const [reports, setReports] = useState<ViolationReport[]>([]);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedNumber, setSelectedNumber] = useState<number>(10);

    // فیلتری که دستیار سامانه فرستاده (از گفتگو یا با صدا) را برمی‌دارد
    useAssistantFilter("violations", (filter) => {
        if (filter.pageSize) setSelectedNumber(filter.pageSize);
        setSearchTerm(filter.search || "");
        setCurrentPage(1);
    });

    // Lightbox states
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);
    const [galleryImages, setGalleryImages] = useState<OrderImage[]>([]);

    // مودال‌های گالری
    const [showDriverGallery, setShowDriverGallery] = useState(false);
    const [driverImages, setDriverImages] = useState<OrderImage[]>([]);
    const [showInspectorGallery, setShowInspectorGallery] = useState(false);
    const [inspectorImages, setInspectorImages] = useState<OrderImage[]>([]);

    // مودال موقعیت
    const [showMapModal, setShowMapModal] = useState(false);
    const [mapLat, setMapLat] = useState<number | null>(null);
    const [mapLng, setMapLng] = useState<number | null>(null);

    // ========== Fetch Data (صفحه‌بندی شده) ==========
    const fetchReports = useCallback(async (page: number, size: number) => {
        setLoading(true);
        try {
            const params: any = { page, size };
            if (searchTerm.trim()) params.search = searchTerm.trim();
            const res = await axios.get<ApiResponse>(
                "https://rasad-api.havirard.ir/monitoring/violation-reports/",
                {
                    params,
                    headers: { Authorization: `Bearer ${localStorage.getItem("app_havir")}` },
                }
            );
            setReports(res.data.items ?? []);
            setTotal(res.data.total ?? 0);
            setTotalPages(res.data.pages ?? 0);
        } catch (err: any) {
            toast.error("خطا در دریافت لیست تخلفات");
            setReports([]);
        } finally {
            setLoading(false);
        }
    }, [searchTerm]);

    // ========== دریافت همه‌ی داده‌ها برای اکسل ==========
    const fetchAllReports = async (search?: string) => {
        let allItems: ViolationReport[] = [];
        let page = 1;
        const size = 100; // حداکثر سایز مجاز
        let hasMore = true;

        while (hasMore) {
            try {
                const res = await axios.get<ApiResponse>(
                    `https://rasad-api.havirard.ir/monitoring/violation-reports/`,
                    {
                        params: {
                            page,
                            size,
                            ...(search?.trim() && { search: search.trim() }),
                            _t: Date.now(),
                        },
                        headers: { Authorization: `Bearer ${localStorage.getItem("app_havir")}` },
                    }
                );
                const items = res.data.items || [];
                allItems = [...allItems, ...items];
                if (items.length < size) {
                    hasMore = false;
                } else {
                    page++;
                }
            } catch (error) {
                console.error("خطا در دریافت صفحه", page, error);
                hasMore = false;
                throw error;
            }
        }
        return allItems;
    };

    // ========== خروجی اکسل (همه داده‌ها) ==========
    const handleExportExcel = async () => {
        try {
            Swal.fire({
                title: "در حال دریافت اطلاعات",
                text: "در حال دریافت تمام داده‌ها...",
                icon: "info",
                allowOutsideClick: false,
                didOpen: () => Swal.showLoading(),
            });

            const allReports = await fetchAllReports(searchTerm || undefined);

            if (allReports.length === 0) {
                Swal.close();
                toast.warning("داده‌ای برای صادرات وجود ندارد");
                return;
            }

            // تابع کمکی برای فرمت اعداد اعشاری
            const truncateDecimal = (val: number | null | undefined): string => {
                if (val == null) return "-";
                const str = val.toString();
                const dot = str.indexOf(".");
                if (dot === -1) return str + ".0";
                return str.substring(0, dot + 2);
            };

            const exportData = allReports.map((r) => ({
                "وضعیت": r.status || "-",
                "شهرستان": r.city || "-",
                "نام نانوا": r.baker_name || "-",
                "کد نانوا": r.baker_code ?? "-",
                "خارج از شبکه": truncateDecimal(r.out_of_network),
                "کم‌فروشی": truncateDecimal(r.under_baking),
                "مرحله توزیع": r.distribution_stage || "-",
                "سهمیه": truncateDecimal(r.quota),
                "تاریخ تخلیه": r.discharge_date_jalali || "-",
                "تعداد تخلیه": truncateDecimal(r.unloaded_count),
                "تاریخ بازرسی": r.inspection_date_jalali || "-",
                "تعداد بازرسی": r.inspector_counted_count ?? "-",
                "فاصله زمانی (روز)": truncateDecimal(r.time_gap_days),
                "مصرف مجاز": truncateDecimal(r.allowed_consumption),
                "تطابق مکانی": r.location_match || "-",
            }));

            exportToExcel(exportData, "ViolationReports-Full");
            toast.success(`${exportData.length} رکورد با موفقیت صادر شد`);
            Swal.close();
        } catch (error: any) {
            const errorMsg = error.response?.data?.message || "خطا در دریافت داده‌ها";
            toast.error(translateErrorMessage(errorMsg));
            Swal.close();
        }
    };

    useEffect(() => {
        // پاک‌سازی اولیه
        document.body.classList.remove('overflow-hidden');
        document.body.style.overflow = '';

        const observer = new MutationObserver(() => {
            if (document.body.classList.contains('overflow-hidden')) {
                document.body.classList.remove('overflow-hidden');
                document.body.style.overflow = '';
            }
        });

        observer.observe(document.body, {
            attributes: true,
            attributeFilter: ['class'],
        });

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        fetchReports(currentPage, selectedNumber);
    }, [currentPage, selectedNumber, fetchReports]);

    const handlePageChange = (page: number) => setCurrentPage(page);
    const handleSizeChange = (value: number) => {
        setSelectedNumber(value);
        setCurrentPage(1);
    };
    const handleSearchChange = (val: string) => {
        setSearchTerm(val);
        setCurrentPage(1);
    };

    // ========== Helpers ==========
    const truncateDecimal = (val: number | null | undefined): string => {
        if (val == null) return "-";
        const str = val.toString();
        const dot = str.indexOf(".");
        if (dot === -1) return str + ".0";
        return str.substring(0, dot + 2);
    };

    const getStatusColor = (status: string) => {
        if (status === "خارج از شبکه") return "text-red-600";
        if (status === "کم‌فروشی") return "text-orange-600";
        return "text-green-600";
    };

    // ========== Columns ==========
    const columns = useMemo(
        () => [
            {
                header: "وضعیت",
                accessorKey: "status",
                enableColumnFilter: false,
                cell: (cell: any) => (
                    <span className={`font-medium ${getStatusColor(cell.getValue())}`}>
                        {cell.getValue() || "-"}
                    </span>
                ),
            },
            {
                header: "شهرستان",
                accessorKey: "city",
                enableColumnFilter: false,
                cell: (cell: any) => cell.getValue() || "-",
            },
            {
                header: "نام نانوا",
                accessorKey: "baker_name",
                enableColumnFilter: false,
                cell: (cell: any) => cell.getValue() || "-",
            },
            {
                header: "خارج از شبکه",
                accessorKey: "out_of_network",
                enableColumnFilter: false,
                cell: (cell: any) => truncateDecimal(cell.getValue()),
            },
            {
                header: "کم‌فروشی",
                accessorKey: "under_baking",
                enableColumnFilter: false,
                cell: (cell: any) => truncateDecimal(cell.getValue()),
            },
            {
                header: "مرحله توزیع",
                accessorKey: "distribution_stage",
                enableColumnFilter: false,
                cell: (cell: any) => cell.getValue() || "-",
            },
            {
                header: "سهمیه",
                accessorKey: "quota",
                enableColumnFilter: false,
                cell: (cell: any) => truncateDecimal(cell.getValue()),
            },
            {
                header: "تاریخ تخلیه",
                accessorKey: "discharge_date_jalali",
                enableColumnFilter: false,
                cell: (cell: any) => cell.getValue() || "-",
            },
            {
                header: "تعداد تخلیه",
                accessorKey: "unloaded_count",
                enableColumnFilter: false,
                cell: (cell: any) => truncateDecimal(cell.getValue()),
            },
            {
                header: "تصاویر راننده",
                accessorKey: "driver_images",
                enableColumnFilter: false,
                cell: (cell: any) => {
                    const images = cell.getValue() as OrderImage[];
                    return images && images.length > 0 ? (
                        <button
                            onClick={() => {
                                setDriverImages(images);
                                setShowDriverGallery(true);
                            }}
                            className="text-blue-600 hover:underline"
                        >
                            <Image size={14} className="inline mr-1" />
                            {images.length} عکس
                        </button>
                    ) : (
                        "-"
                    );
                },
            },
            {
                header: "تاریخ بازرسی",
                accessorKey: "inspection_date_jalali",
                enableColumnFilter: false,
                cell: (cell: any) => cell.getValue() || "-",
            },
            {
                header: "تعداد بازرسی",
                accessorKey: "inspector_counted_count",
                enableColumnFilter: false,
                cell: (cell: any) => cell.getValue() ?? "-",
            },
            {
                header: "تصاویر بازرس",
                accessorKey: "inspector_images",
                enableColumnFilter: false,
                cell: (cell: any) => {
                    const images = cell.getValue() as OrderImage[];
                    return images && images.length > 0 ? (
                        <button
                            onClick={() => {
                                setInspectorImages(images);
                                setShowInspectorGallery(true);
                            }}
                            className="text-blue-600 hover:underline"
                        >
                            <Image size={14} className="inline mr-1" />
                            {images.length} عکس
                        </button>
                    ) : (
                        "-"
                    );
                },
            },
            {
                header: "فاصله زمانی (روز)",
                accessorKey: "time_gap_days",
                enableColumnFilter: false,
                cell: (cell: any) => truncateDecimal(cell.getValue()),
            },
            {
                header: "مصرف مجاز",
                accessorKey: "allowed_consumption",
                enableColumnFilter: false,
                cell: (cell: any) => truncateDecimal(cell.getValue()),
            },
            {
                header: "موقعیت راننده",
                enableColumnFilter: false,
                cell: (cell: any) => {
                    const report = cell.row.original as ViolationReport;
                    if (report.driver_location_lat && report.driver_location_lng) {
                        return (
                            <button
                                onClick={() => {
                                    setMapLat(report.driver_location_lat);
                                    setMapLng(report.driver_location_lng);
                                    setShowMapModal(true);
                                }}
                                className="text-blue-600 hover:underline"
                            >
                                <MapPin size={14} className="inline mr-1" />
                                نمایش
                            </button>
                        );
                    }
                    return "-";
                },
            },
            {
                header: "موقعیت بازرس",
                enableColumnFilter: false,
                cell: (cell: any) => {
                    const report = cell.row.original as ViolationReport;
                    if (report.inspector_location_lat && report.inspector_location_lng) {
                        return (
                            <button
                                onClick={() => {
                                    setMapLat(report.inspector_location_lat);
                                    setMapLng(report.inspector_location_lng);
                                    setShowMapModal(true);
                                }}
                                className="text-blue-600 hover:underline"
                            >
                                <MapPin size={14} className="inline mr-1" />
                                نمایش
                            </button>
                        );
                    }
                    return "-";
                },
            },
            {
                header: "تطابق مکانی",
                enableColumnFilter: false,
                cell: (cell: any) => {
                    const report = cell.row.original as ViolationReport;
                    const isMatch = report.location_match === "تطابق";
                    return (
                        <div className="flex items-center gap-2">
                            <span className={isMatch ? "text-green-600" : "text-red-600"}>
                                {report.location_match || "-"}
                            </span>
                            {report.driver_location_lat && report.inspector_location_lat && (
                                <button
                                    onClick={() => {
                                        setMapLat(report.driver_location_lat);
                                        setMapLng(report.driver_location_lng);
                                        setShowMapModal(true);
                                    }}
                                    className="text-xs text-blue-600 hover:underline"
                                >
                                    (نمایش)
                                </button>
                            )}
                        </div>
                    );
                },
            },
        ],
        []
    );

    return (
        <React.Fragment>
            <BreadCrumb pageTitle="گزارش تخلفات" title="پایش آرد" />
            <ToastContainer position="top-center" closeButton={false} limit={3} rtl={true} />

            <div className="card">
                <div className="card-body">
                    <div className="flex flex-col gap-4 mb-4 md:flex-row md:items-center md:justify-between">
                        <div className="flex items-center gap-2">
                            <RefreshCw
                                className={`size-5 text-blue-500 cursor-pointer ${loading ? "animate-spin" : ""}`}
                                onClick={() => fetchReports(currentPage, selectedNumber)}
                            />
                            <h6 className="text-lg font-semibold">
                                مجموع تخلفات{" "}
                                <span className="px-2 py-0.5 text-sm bg-blue-100 text-blue-700 rounded-full">
                                    {total}
                                </span>
                            </h6>
                        </div>
                        <div className="flex flex-wrap gap-3 items-center">
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="جستجو..."
                                    value={searchTerm}
                                    onChange={(e) => handleSearchChange(e.target.value)}
                                    className="w-64 pr-10 pl-4 py-2 text-sm border rounded-xl focus:ring-2 focus:ring-blue-500 dark:bg-gray-800"
                                />
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-400" />
                                {searchTerm && (
                                    <button onClick={() => handleSearchChange("")} className="absolute right-3 top-1/2 -translate-y-1/2">
                                        <X className="size-4" />
                                    </button>
                                )}
                            </div>
                            <TableDropDownSmall onSelect={handleSizeChange} />
                            <button onClick={handleExportExcel} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-white border border-blue-200 rounded-xl hover:bg-blue-50">
                                <Download size={16} /> اکسل
                            </button>
                        </div>
                    </div>

                    {loading ? (
                        <LoadingCounter count={0} text="در حال بارگذاری..." />
                    ) : reports.length === 0 ? (
                        <LoadingCounter count={0} text="تخلفی یافت نشد" />
                    ) : (
                        <>
                            <TableContainer
                                isPagination={false}
                                columns={columns}
                                data={reports}
                                customPageSize={selectedNumber}
                                divclassName="-mx-5 overflow-x-auto"
                                tableclassName="w-full whitespace-nowrap"
                                theadclassName="ltr:text-left rtl:text-right bg-slate-100 dark:bg-zink-600"
                                thclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-b border-slate-200 dark:border-zink-500"
                                tdclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 border-y border-slate-200 dark:border-zink-500"
                                PaginationClassName="hidden"
                            />
                            <PaginationTest
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={handlePageChange}
                            />
                        </>
                    )}
                </div>
            </div>

            {/* مودال گالری تصاویر راننده */}
            <Modal show={showDriverGallery} onHide={() => setShowDriverGallery(false)} modal-center="true"
                className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-drawer"
                dialogClassName="w-[90vw] md:w-[36rem] bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl text-white">
                <Modal.Header className="flex items-center justify-between p-5 border-b border-white/10">
                    <Modal.Title className="text-lg text-white font-bold">
                        جهت بزرگنمایی روی عکس کلیک کنید
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-5 max-h-[70vh] overflow-y-auto">
                    <div className="grid grid-cols-2 gap-4">
                        {driverImages.map((img, idx) => (
                            <div
                                key={img.id}
                                className="border border-white/10 rounded-xl overflow-hidden cursor-pointer hover:scale-[1.02] transition-transform"
                                onClick={() => {
                                    setLightboxIndex(idx);
                                    setGalleryImages(driverImages);
                                    setLightboxOpen(true);
                                }}
                            >
                                <img src={img.url} alt={img.file_name} className="w-full h-40 object-cover" />
                            </div>
                        ))}
                    </div>
                </Modal.Body>
            </Modal>

            {/* مودال گالری تصاویر بازرس */}
            <Modal show={showInspectorGallery} onHide={() => setShowInspectorGallery(false)} modal-center="true"
                className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-drawer"
                dialogClassName="w-[90vw] md:w-[36rem] bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl text-white">
                <Modal.Header className="flex items-center justify-between p-5 border-b border-white/10">
                    <p className="text-sm text-white/70 mt-1">
                        جهت بزرگنمایی روی عکس کلیک کنید
                    </p>
                </Modal.Header>
                <Modal.Body className="p-5 max-h-[70vh] overflow-y-auto">
                    <div className="grid grid-cols-2 gap-4">
                        {inspectorImages.map((img, idx) => (
                            <div
                                key={img.id}
                                className="border border-white/10 rounded-xl overflow-hidden cursor-pointer hover:scale-[1.02] transition-transform"
                                onClick={() => {
                                    setLightboxIndex(idx);
                                    setGalleryImages(inspectorImages);
                                    setLightboxOpen(true);
                                }}
                            >
                                <img src={img.url} alt={img.file_name} className="w-full h-40 object-cover" />
                            </div>
                        ))}
                    </div>
                </Modal.Body>
            </Modal>

            {/* مودال نقشه */}
            <Modal show={showMapModal} onHide={() => setShowMapModal(false)} modal-center="true"
                className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
                dialogClassName="w-screen md:w-[42rem] bg-white rounded-2xl shadow-2xl dark:bg-gray-800">
                <Modal.Header className="flex items-center justify-between p-5 border-b dark:border-gray-700">
                    <Modal.Title className="text-lg font-bold">موقعیت مکانی</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-5 max-h-[70vh] overflow-y-auto">
                    {mapLat != null && mapLng != null ? (
                        <StaticMapMarker
                            lat={mapLat} lng={mapLng}
                            zoom={17}
                            height={300}
                        />
                    ) : (
                        <p className="text-gray-400">موقعیت ثبت نشده است.</p>
                    )}
                </Modal.Body>
            </Modal>

            {/* Lightbox تمام‌صفحه */}
            {lightboxOpen && (
                <ImageLightbox
                    images={galleryImages}
                    initialIndex={lightboxIndex}
                    onClose={() => setLightboxOpen(false)}
                />
            )}
        </React.Fragment>
    );
};

export default ViolationReports;
// import React, { useCallback, useEffect, useMemo, useState } from "react";
// import BreadCrumb from "Common/BreadCrumb";
// import { Search, X, Download, RefreshCw, Image, MapPin } from "lucide-react";
// import TableContainer from "Common/TableContainer";
// import Modal from "Common/Components/Modal";
// import { toast, ToastContainer } from "react-toastify";
// import TableDropDownSmall from "pages/Components/TableDropDownSmall";
// import { exportToExcel } from "Common/Components/Excel";
// import LoadingCounter from "Common/Components/LoadingCounterProps";
// import PaginationTest from "Common/Components/Paginationtest";
// import axios from "axios";
// import StaticMapMarker from "Common/Components/StaticArcMap";
// import ImageLightbox from "Common/Components/ImageLightbox";

// // ========== Types ==========
// interface OrderImage {
//     id: number;
//     url: string;
//     file_name: string;
//     category: string;
// }

// interface ViolationReport {
//     id: number;
//     status: string;
//     city: string;
//     baker_name: string;
//     out_of_network: number;
//     under_baking: number;
//     distribution_stage: string;
//     quota: number;
//     discharge_date_jalali: string;
//     unloaded_count: number;
//     driver_images: OrderImage[];
//     inspection_date_jalali: string;
//     inspector_counted_count: number;
//     inspector_images: OrderImage[];
//     time_gap_days: number;
//     allowed_consumption: number;
//     driver_location_lat: number | null;
//     driver_location_lng: number | null;
//     inspector_location_lat: number | null;
//     inspector_location_lng: number | null;
//     location_match: string;
//     baker_code: number

// }

// interface ApiResponse {
//     total: number;
//     size: number;
//     pages: number;
//     items: ViolationReport[];
// }

// const ViolationReports = () => {
//     const [reports, setReports] = useState<ViolationReport[]>([]);
//     const [loading, setLoading] = useState(false);
//     const [total, setTotal] = useState(0);
//     const [totalPages, setTotalPages] = useState(0);

//     const [searchTerm, setSearchTerm] = useState("");
//     const [currentPage, setCurrentPage] = useState(1);
//     const [selectedNumber, setSelectedNumber] = useState<number>(10);

//     // Lightbox states
//     const [lightboxOpen, setLightboxOpen] = useState(false);
//     const [lightboxIndex, setLightboxIndex] = useState(0);
//     const [galleryImages, setGalleryImages] = useState<OrderImage[]>([]);

//     // مودال‌های گالری
//     const [showDriverGallery, setShowDriverGallery] = useState(false);
//     const [driverImages, setDriverImages] = useState<OrderImage[]>([]);
//     const [showInspectorGallery, setShowInspectorGallery] = useState(false);
//     const [inspectorImages, setInspectorImages] = useState<OrderImage[]>([]);

//     // مودال موقعیت
//     const [showMapModal, setShowMapModal] = useState(false);
//     const [mapLat, setMapLat] = useState<number | null>(null);
//     const [mapLng, setMapLng] = useState<number | null>(null);

//     // ========== Fetch Data ==========
//     const fetchReports = useCallback(async (page: number, size: number) => {
//         setLoading(true);
//         try {
//             const params: any = { page, size };
//             if (searchTerm.trim()) params.search = searchTerm.trim();
//             const res = await axios.get<ApiResponse>(
//                 "https://rasad-api.havirard.ir/monitoring/violation-reports/",
//                 {
//                     params,
//                     headers: { Authorization: `Bearer ${localStorage.getItem("app_havir")}` },
//                 }
//             );
//             setReports(res.data.items ?? []);
//             setTotal(res.data.total ?? 0);
//             setTotalPages(res.data.pages ?? 0);
//         } catch (err: any) {
//             toast.error("خطا در دریافت لیست تخلفات");
//             setReports([]);
//         } finally {
//             setLoading(false);
//         }
//     }, [searchTerm]);
//     useEffect(() => {
//         // پاک‌سازی اولیه
//         document.body.classList.remove('overflow-hidden');
//         document.body.style.overflow = '';

//         // نگهبان: اگر کلاس overflow-hidden دوباره اضافه شد، بلافاصله حذف کن
//         const observer = new MutationObserver(() => {
//             if (document.body.classList.contains('overflow-hidden')) {
//                 document.body.classList.remove('overflow-hidden');
//                 document.body.style.overflow = '';
//             }
//         });

//         observer.observe(document.body, {
//             attributes: true,
//             attributeFilter: ['class'],
//         });

//         return () => observer.disconnect();
//     }, []);
//     useEffect(() => {
//         fetchReports(currentPage, selectedNumber);
//     }, [currentPage, selectedNumber, fetchReports]);

//     const handlePageChange = (page: number) => setCurrentPage(page);
//     const handleSizeChange = (value: number) => {
//         setSelectedNumber(value);
//         setCurrentPage(1);
//     };
//     const handleSearchChange = (val: string) => {
//         setSearchTerm(val);
//         setCurrentPage(1);
//     };

//     // ========== Helpers ==========
//     const truncateDecimal = (val: number | null | undefined): string => {
//         if (val == null) return "-";
//         const str = val.toString();
//         const dot = str.indexOf(".");
//         if (dot === -1) return str + ".0";
//         return str.substring(0, dot + 2);
//     };

//     const getStatusColor = (status: string) => {
//         if (status === "خارج از شبکه") return "text-red-600";
//         if (status === "کم‌فروشی") return "text-orange-600";
//         return "text-green-600";
//     };

//     // ========== Columns ==========
//     const columns = useMemo(
//         () => [
//             {
//                 header: "وضعیت",
//                 accessorKey: "status",
//                 enableColumnFilter: false,
//                 cell: (cell: any) => (
//                     <span className={`font-medium ${getStatusColor(cell.getValue())}`}>
//                         {cell.getValue() || "-"}
//                     </span>
//                 ),
//             },
//             {
//                 header: "شهرستان",
//                 accessorKey: "city",
//                 enableColumnFilter: false,
//                 cell: (cell: any) => cell.getValue() || "-",
//             },
//             {
//                 header: "نام نانوا",
//                 accessorKey: "baker_name",
//                 enableColumnFilter: false,
//                 cell: (cell: any) => cell.getValue() || "-",
//             },
//             {
//                 header: "خارج از شبکه",
//                 accessorKey: "out_of_network",
//                 enableColumnFilter: false,
//                 cell: (cell: any) => truncateDecimal(cell.getValue()),
//             },
//             {
//                 header: "کم‌فروشی",
//                 accessorKey: "under_baking",
//                 enableColumnFilter: false,
//                 cell: (cell: any) => truncateDecimal(cell.getValue()),
//             },
//             {
//                 header: "مرحله توزیع",
//                 accessorKey: "distribution_stage",
//                 enableColumnFilter: false,
//                 cell: (cell: any) => cell.getValue() || "-",
//             },
//             {
//                 header: "سهمیه",
//                 accessorKey: "quota",
//                 enableColumnFilter: false,
//                 cell: (cell: any) => truncateDecimal(cell.getValue()),
//             },
//             {
//                 header: "تاریخ تخلیه",
//                 accessorKey: "discharge_date_jalali",
//                 enableColumnFilter: false,
//                 cell: (cell: any) => cell.getValue() || "-",
//             },
//             {
//                 header: "تعداد تخلیه",
//                 accessorKey: "unloaded_count",
//                 enableColumnFilter: false,
//                 cell: (cell: any) => truncateDecimal(cell.getValue()),
//             },
//             {
//                 header: "تصاویر راننده",
//                 accessorKey: "driver_images",
//                 enableColumnFilter: false,
//                 cell: (cell: any) => {
//                     const images = cell.getValue() as OrderImage[];
//                     return images && images.length > 0 ? (
//                         <button
//                             onClick={() => {
//                                 setDriverImages(images);
//                                 setShowDriverGallery(true);
//                             }}
//                             className="text-blue-600 hover:underline"
//                         >
//                             <Image size={14} className="inline mr-1" />
//                             {images.length} عکس
//                         </button>
//                     ) : (
//                         "-"
//                     );
//                 },
//             },
//             {
//                 header: "تاریخ بازرسی",
//                 accessorKey: "inspection_date_jalali",
//                 enableColumnFilter: false,
//                 cell: (cell: any) => cell.getValue() || "-",
//             },
//             {
//                 header: "تعداد بازرسی",
//                 accessorKey: "inspector_counted_count",
//                 enableColumnFilter: false,
//                 cell: (cell: any) => cell.getValue() ?? "-",
//             },
//             {
//                 header: "تصاویر بازرس",
//                 accessorKey: "inspector_images",
//                 enableColumnFilter: false,
//                 cell: (cell: any) => {
//                     const images = cell.getValue() as OrderImage[];
//                     return images && images.length > 0 ? (
//                         <button
//                             onClick={() => {
//                                 setInspectorImages(images);
//                                 setShowInspectorGallery(true);
//                             }}
//                             className="text-blue-600 hover:underline"
//                         >
//                             <Image size={14} className="inline mr-1" />
//                             {images.length} عکس
//                         </button>
//                     ) : (
//                         "-"
//                     );
//                 },
//             },
//             {
//                 header: "فاصله زمانی (روز)",
//                 accessorKey: "time_gap_days",
//                 enableColumnFilter: false,
//                 cell: (cell: any) => truncateDecimal(cell.getValue()),
//             },
//             {
//                 header: "مصرف مجاز",
//                 accessorKey: "allowed_consumption",
//                 enableColumnFilter: false,
//                 cell: (cell: any) => truncateDecimal(cell.getValue()),
//             },
//             {
//                 header: "موقعیت راننده",
//                 enableColumnFilter: false,
//                 cell: (cell: any) => {
//                     const report = cell.row.original as ViolationReport;
//                     if (report.driver_location_lat && report.driver_location_lng) {
//                         return (
//                             <button
//                                 onClick={() => {
//                                     setMapLat(report.driver_location_lat);
//                                     setMapLng(report.driver_location_lng);
//                                     setShowMapModal(true);
//                                 }}
//                                 className="text-blue-600 hover:underline"
//                             >
//                                 <MapPin size={14} className="inline mr-1" />
//                                 نمایش
//                             </button>
//                         );
//                     }
//                     return "-";
//                 },
//             },
//             {
//                 header: "موقعیت بازرس",
//                 enableColumnFilter: false,
//                 cell: (cell: any) => {
//                     const report = cell.row.original as ViolationReport;
//                     if (report.inspector_location_lat && report.inspector_location_lng) {
//                         return (
//                             <button
//                                 onClick={() => {
//                                     setMapLat(report.inspector_location_lat);
//                                     setMapLng(report.inspector_location_lng);
//                                     setShowMapModal(true);
//                                 }}
//                                 className="text-blue-600 hover:underline"
//                             >
//                                 <MapPin size={14} className="inline mr-1" />
//                                 نمایش
//                             </button>
//                         );
//                     }
//                     return "-";
//                 },
//             },
//             {
//                 header: "تطابق مکانی",
//                 enableColumnFilter: false,
//                 cell: (cell: any) => {
//                     const report = cell.row.original as ViolationReport;
//                     const isMatch = report.location_match === "تطابق";
//                     return (
//                         <div className="flex items-center gap-2">
//                             <span className={isMatch ? "text-green-600" : "text-red-600"}>
//                                 {report.location_match || "-"}
//                             </span>
//                             {report.driver_location_lat && report.inspector_location_lat && (
//                                 <button
//                                     onClick={() => {
//                                         setMapLat(report.driver_location_lat);
//                                         setMapLng(report.driver_location_lng);
//                                         setShowMapModal(true);
//                                     }}
//                                     className="text-xs text-blue-600 hover:underline"
//                                 >
//                                     (نمایش)
//                                 </button>
//                             )}
//                         </div>
//                     );
//                 },
//             },
//         ],
//         []
//     );

//     const handleExportExcel = () => {
//         const exportData = reports.map((r) => ({
//             "وضعیت": r.status,
//             "شهرستان": r.city,
//             "نام نانوا": r.baker_name,
//             "کد نانوا": r.baker_code,
//             "خارج از شبکه": truncateDecimal(r.out_of_network),
//             "کم‌فروشی": truncateDecimal(r.under_baking),
//             "مرحله توزیع": r.distribution_stage,
//             "سهمیه": truncateDecimal(r.quota),
//             "تاریخ تخلیه": r.discharge_date_jalali,
//             "تعداد تخلیه": truncateDecimal(r.unloaded_count),
//             "تاریخ بازرسی": r.inspection_date_jalali,
//             "تعداد بازرسی": r.inspector_counted_count,
//             "فاصله زمانی (روز)": truncateDecimal(r.time_gap_days),
//             "مصرف مجاز": truncateDecimal(r.allowed_consumption),
//             "تطابق مکانی": r.location_match,
//         }));
//         exportToExcel(exportData, "ViolationReports");
//     };

//     return (
//         <React.Fragment>
//             <BreadCrumb pageTitle="گزارش تخلفات" title="پایش آرد" />
//             <ToastContainer position="top-center" closeButton={false} limit={3} rtl={true} />

//             <div className="card">
//                 <div className="card-body">
//                     <div className="flex flex-col gap-4 mb-4 md:flex-row md:items-center md:justify-between">
//                         <div className="flex items-center gap-2">
//                             <RefreshCw
//                                 className={`size-5 text-blue-500 cursor-pointer ${loading ? "animate-spin" : ""}`}
//                                 onClick={() => fetchReports(currentPage, selectedNumber)}
//                             />
//                             <h6 className="text-lg font-semibold">
//                                 مجموع تخلفات{" "}
//                                 <span className="px-2 py-0.5 text-sm bg-blue-100 text-blue-700 rounded-full">
//                                     {total}
//                                 </span>
//                             </h6>
//                         </div>
//                         <div className="flex flex-wrap gap-3 items-center">
//                             <div className="relative">
//                                 <input
//                                     type="text"
//                                     placeholder="جستجو..."
//                                     value={searchTerm}
//                                     onChange={(e) => handleSearchChange(e.target.value)}
//                                     className="w-64 pr-10 pl-4 py-2 text-sm border rounded-xl focus:ring-2 focus:ring-blue-500 dark:bg-gray-800"
//                                 />
//                                 <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-400" />
//                                 {searchTerm && (
//                                     <button onClick={() => handleSearchChange("")} className="absolute right-3 top-1/2 -translate-y-1/2">
//                                         <X className="size-4" />
//                                     </button>
//                                 )}
//                             </div>
//                             <TableDropDownSmall onSelect={handleSizeChange} />
//                             <button onClick={handleExportExcel} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-white border border-blue-200 rounded-xl hover:bg-blue-50">
//                                 <Download size={16} /> اکسل
//                             </button>
//                         </div>
//                     </div>

//                     {loading ? (
//                         <LoadingCounter count={0} text="در حال بارگذاری..." />
//                     ) : reports.length === 0 ? (
//                         <LoadingCounter count={0} text="تخلفی یافت نشد" />
//                     ) : (
//                         <>
//                             <TableContainer
//                                 isPagination={false}
//                                 columns={columns}
//                                 data={reports}
//                                 customPageSize={selectedNumber}
//                                 divclassName="-mx-5 overflow-x-auto"
//                                 tableclassName="w-full whitespace-nowrap"
//                                 theadclassName="ltr:text-left rtl:text-right bg-slate-100 dark:bg-zink-600"
//                                 thclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-b border-slate-200 dark:border-zink-500"
//                                 tdclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 border-y border-slate-200 dark:border-zink-500"
//                                 PaginationClassName="hidden"
//                             />
//                             <PaginationTest
//                                 currentPage={currentPage}
//                                 totalPages={totalPages}
//                                 onPageChange={handlePageChange}
//                             />
//                         </>
//                     )}
//                 </div>
//             </div>

//             {/* مودال گالری تصاویر راننده */}
//             <Modal show={showDriverGallery} onHide={() => setShowDriverGallery(false)} modal-center="true"
//                 className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-drawer"
//                 dialogClassName="w-[90vw] md:w-[36rem] bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl text-white">
//                 <Modal.Header className="flex items-center justify-between p-5 border-b border-white/10">
//                     <Modal.Title className="text-lg text-white font-bold">

//                         جهت بزرگنمایی روی عکس کلیک کنید
//                     </Modal.Title>
//                     {/* <p className="text-sm text-white/70 mt-1">
//                     </p>          */}
//                 </Modal.Header>
//                 <Modal.Body className="p-5 max-h-[70vh] overflow-y-auto">
//                     <div className="grid grid-cols-2 gap-4">
//                         {driverImages.map((img, idx) => (
//                             <div
//                                 key={img.id}
//                                 className="border border-white/10 rounded-xl overflow-hidden cursor-pointer hover:scale-[1.02] transition-transform"
//                                 onClick={() => {
//                                     setLightboxIndex(idx);
//                                     setGalleryImages(driverImages);
//                                     setLightboxOpen(true);
//                                 }}
//                             >
//                                 <img src={img.url} alt={img.file_name} className="w-full h-40 object-cover" />
//                             </div>
//                         ))}
//                     </div>
//                 </Modal.Body>
//             </Modal>

//             {/* مودال گالری تصاویر بازرس */}
//             <Modal show={showInspectorGallery} onHide={() => setShowInspectorGallery(false)} modal-center="true"
//                 className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-drawer"
//                 dialogClassName="w-[90vw] md:w-[36rem] bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl text-white">
//                 <Modal.Header className="flex items-center justify-between p-5 border-b border-white/10">
//                     {/* <Modal.Title className="text-lg text-white font-bold">تصاویر بازرس</Modal.Title> */}
//                     {/* <Modal.Title className="text-lg text-white font-bold">جهت بزرگنمایی روی عکس کلیک کنید </Modal.Title> */}
//                     <p className="text-sm text-white/70 mt-1">
//                         جهت بزرگنمایی  روی عکس کلیک کنید
//                     </p>
//                 </Modal.Header>
//                 <Modal.Body className="p-5 max-h-[70vh] overflow-y-auto">
//                     <div className="grid grid-cols-2 gap-4">
//                         {inspectorImages.map((img, idx) => (
//                             <div
//                                 key={img.id}
//                                 className="border border-white/10 rounded-xl overflow-hidden cursor-pointer hover:scale-[1.02] transition-transform"
//                                 onClick={() => {
//                                     setLightboxIndex(idx);
//                                     setGalleryImages(inspectorImages);
//                                     setLightboxOpen(true);
//                                 }}
//                             >
//                                 <img src={img.url} alt={img.file_name} className="w-full h-40 object-cover" />
//                             </div>
//                         ))}
//                     </div>
//                 </Modal.Body>
//             </Modal>

//             {/* مودال نقشه */}
//             <Modal show={showMapModal} onHide={() => setShowMapModal(false)} modal-center="true"
//                 className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
//                 dialogClassName="w-screen md:w-[42rem] bg-white rounded-2xl shadow-2xl dark:bg-gray-800">
//                 <Modal.Header className="flex items-center justify-between p-5 border-b dark:border-gray-700">
//                     <Modal.Title className="text-lg font-bold">موقعیت مکانی</Modal.Title>
//                 </Modal.Header>
//                 <Modal.Body className="p-5 max-h-[70vh] overflow-y-auto">
//                     {mapLat != null && mapLng != null ? (
//                         <StaticMapMarker
//                             lat={mapLat} lng={mapLng}
//                             zoom={17}
//                             width={800}
//                             height={300}
//                             apiKey="service.b30c3de70ec34167a3a9c8dabb96bd10"
//                         />
//                     ) : (
//                         <p className="text-gray-400">موقعیت ثبت نشده است.</p>
//                     )}
//                 </Modal.Body>
//             </Modal>

//             {/* Lightbox تمام‌صفحه */}
//             {lightboxOpen && (
//                 <ImageLightbox
//                     images={galleryImages}
//                     initialIndex={lightboxIndex}
//                     onClose={() => setLightboxOpen(false)}
//                 />
//             )}
//         </React.Fragment>
//     );
// };

// export default ViolationReports;